/* eslint-disable react-hooks/exhaustive-deps */
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import formatDate from "../../../../../lib/formatDate";
import { useDropdown } from "../../../../../components/ui/dropdownwrapper/DropdownWrapper";
import { useLazyGetAllTimeSheetRecordsQuery } from "../../../../../app/services/timeSheetLogAPI";

import Iconify from "../../../iconify/iconify";
import Tooltip from "../../../../../components/ui/tooltip/Tooltip";
import Buttons from "../../../../../components/ui/button/Buttons";
import CenterModel from "../../../../../components/ui/centerModel/CenterModel";

import "./HeaderTimesheetPanel.scss";

const REQUIRED_HOURS_PER_DAY = 8.5;

const AddUpdateTimeLog = lazy(() =>
  import("../../../../../components/addUpdateTimelog/AddUpdateTimeLog")
);

const formatWorkingHours = (hours) => {
  if (hours === null || hours === undefined || hours === "") {
    return "0h 0m";
  }
  if (typeof hours === "string" && hours.toLowerCase().includes("h")) {
    return hours;
  }
  const value = Number(hours);
  if (Number.isNaN(value)) {
    return "0h 0m";
  }
  const h = Math.floor(value);
  const m = Math.round((value - h) * 60);
  return `${h}h ${m}m`;
};

const formatLogTime = (time) => {
  if (!time) {
    return "--:--";
  }
  const parsed = moment(time, ["HH:mm:ss", "HH:mm", moment.ISO_8601], true);
  if (parsed.isValid()) {
    return parsed.format("hh:mm A");
  }
  return moment(time).isValid() ? moment(time).format("hh:mm A") : "--:--";
};

const getHoursStatus = (hours) => {
  const value = Number(hours);
  if (!value || Number.isNaN(value) || value <= 0) {
    return "red";
  }
  return value >= REQUIRED_HOURS_PER_DAY ? "green" : "red";
};

const getLogTaskLabel = (log) => {
  return log?.title || log?.projectName || "";
};

const useTextOverflow = (text) => {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element || !text) {
      setIsOverflowing(false);
      return undefined;
    }

    const checkOverflow = () => {
      setIsOverflowing(
        element.scrollHeight > element.clientHeight + 1 ||
        element.scrollWidth > element.clientWidth + 1
      );
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  return { textRef, isOverflowing };
};

const PortaledFullTextTooltip = ({ id, text, isOpen }) => {
  if (!text || !isOpen) {
    return null;
  }

  return createPortal(
    <Tooltip
      targetId={id}
      label={text}
      position="top"
      variant="dark"
      className="header-timesheet-info-tooltip"
      positionStrategy="fixed"
      isOpen={true}
    />,
    document.body
  );
};

const OverflowPill = ({ id, text, className = "" }) => {
  const { textRef, isOverflowing } = useTextOverflow(text);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  if (!text) {
    return null;
  }

  return (
    <>
      <span
        ref={textRef}
        id={id}
        className={`header-timesheet-pill ${className} ${isOverflowing ? "is-truncated" : ""}`.trim()}
        onMouseEnter={() => isOverflowing && setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
      >
        {text}
      </span>
      {isOverflowing && (
        <PortaledFullTextTooltip id={id} text={text} isOpen={isTooltipOpen} />
      )}
    </>
  );
};

const OverflowTextWithInfo = ({ id, text, className = "" }) => {
  const { textRef, isOverflowing } = useTextOverflow(text);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  if (!text) {
    return null;
  }

  const showTooltip = () => {
    if (isOverflowing) {
      setIsTooltipOpen(true);
    }
  };

  const hideTooltip = () => setIsTooltipOpen(false);

  return (
    <div
      id={id}
      className={`header-timesheet-overflow ${className} ${isOverflowing ? "is-truncated" : ""}`.trim()}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      <span ref={textRef} className="header-timesheet-overflow-text">
        {text}
      </span>
      {isOverflowing && (
        <>
          <button
            type="button"
            className="header-timesheet-overflow-info"
            aria-label="View full text"
            onClick={(event) => {
              event.stopPropagation();
              setIsTooltipOpen(true);
            }}
          >
            <Iconify icon="mdi:information-outline" width={16} />
          </button>
          <PortaledFullTextTooltip id={id} text={text} isOpen={isTooltipOpen} />
        </>
      )}
    </div>
  );
};

const HeaderTimesheetPanel = () => {
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);

  const {
    isOpen,
    toggleDropdown,
    closeDropdown,
    dropdownRef,
    toggleElementRef,
    dropdownPosition,
  } = useDropdown("left");

  const [getAllTimeSheetRecords, {
    isLoading,
    isFetching,
    isError,
    isUninitialized,
    data: timeSheetRecordsData,
  }] = useLazyGetAllTimeSheetRecordsQuery();

  const fetchTodayLogs = () => {
    if (!userId) {
      return;
    }
    getAllTimeSheetRecords({
      userId,
      startDate: new Date().toISOString(),
    });
  };

  // Soft-refresh on each open so data stays fresh, but keep prior results on screen.
  useEffect(() => {
    if (isOpen && userId) {
      fetchTodayLogs();
    }
  }, [isOpen, userId]);

  const todayKey = formatDate(new Date(), "YYYY-MM-DD");
  const todayTitle = `Timesheet - ${formatDate(new Date(), "DD MMM YYYY")}`;

  const todayDay = useMemo(() => {
    if (!Array.isArray(timeSheetRecordsData)) {
      return null;
    }
    return timeSheetRecordsData.find(
      (day) => formatDate(day?.startDate, "YYYY-MM-DD") === todayKey
    ) || null;
  }, [timeSheetRecordsData, todayKey]);

  const todayLogs = useMemo(() => {
    const taskList = todayDay?.taskList || todayDay?.TaskList || [];
    return [...taskList].sort((a, b) =>
      String(a?.startTime || "").localeCompare(String(b?.startTime || ""))
    );
  }, [todayDay]);

  const lastEndTime = todayLogs.length
    ? todayLogs[todayLogs.length - 1]?.endTime || null
    : null;

  const hasCachedData = Array.isArray(timeSheetRecordsData);
  // Full-page loader only on first open (no data yet). Later opens refresh quietly.
  const isInitialLoading =
    isOpen && !hasCachedData && (isUninitialized || isLoading || isFetching);
  const isRefreshing = isOpen && hasCachedData && isFetching;
  const totalHoursValue = todayDay?.totalWorkingHours;
  const totalHours = formatWorkingHours(totalHoursValue);
  const hoursStatus = getHoursStatus(totalHoursValue);
  const entryCount = todayLogs.length;
  const entryLabel = `${entryCount} ${entryCount === 1 ? "entry" : "entries"}`;

  const handleViewAll = () => {
    closeDropdown();
    navigate("/TimeSheet/MyTimeSheet");
  };

  const handleAddLog = () => {
    setShowTimesheetModal(true);
  };

  const handleCloseModal = () => {
    setShowTimesheetModal(false);
  };

  const handleAddSuccess = () => {
    setShowTimesheetModal(false);
    fetchTodayLogs();
  };

  const renderBody = () => {
    if (isInitialLoading) {
      return (
        <div className="header-timesheet-flyout-state">
          <div className="header-timesheet-flyout-loader" />
          <p>Loading today's logs...</p>
        </div>
      );
    }

    if (isError && !hasCachedData) {
      return (
        <div className="header-timesheet-flyout-state">
          <Iconify icon="mdi:alert-circle-outline" width={36} className="header-timesheet-flyout-state-icon" />
          <p>Unable to load today's timesheet.</p>
          <span>Please try again.</span>
        </div>
      );
    }

    if (todayLogs.length === 0) {
      return (
        <div className="header-timesheet-flyout-state">
          <Iconify icon="tdesign:time" width={36} className="header-timesheet-flyout-state-icon" />
          <p>No logs for today</p>
          <span>Add a log to start tracking time.</span>
        </div>
      );
    }

    return (
      <ul className="header-timesheet-flyout-list">
        {todayLogs.map((log, index) => (
          <li
            key={log?.timesheetDetailId || `${log?.startTime}-${index}`}
            className="header-timesheet-flyout-item"
          >
            <div className="header-timesheet-flyout-item-content">
              <div className="header-timesheet-flyout-item-top">
                <span className="header-timesheet-flyout-item-time">
                  {formatLogTime(log?.startTime)} – {formatLogTime(log?.endTime)}
                </span>
                <div className="header-timesheet-flyout-item-meta">
                  {(log?.activityName || log?.subActivityName) && (
                    <div className="header-timesheet-flyout-item-pills">
                      {log?.activityName && (
                        <OverflowPill
                          id={`header-ts-activity-${log?.timesheetDetailId || index}`}
                          text={log.activityName}
                          className="header-timesheet-pill--activity"
                        />
                      )}
                      {log?.subActivityName && (
                        <OverflowPill
                          id={`header-ts-sub-${log?.timesheetDetailId || index}`}
                          text={log.subActivityName}
                          className="header-timesheet-pill--sub"
                        />
                      )}
                    </div>
                  )}
                  <span className="header-timesheet-flyout-item-hours">
                    {formatWorkingHours(log?.workingHours)}
                  </span>
                </div>
              </div>
              <OverflowTextWithInfo
                id={`header-ts-task-${log?.timesheetDetailId || index}`}
                text={getLogTaskLabel(log)}
                className="header-timesheet-flyout-item-task"
              />
              <OverflowTextWithInfo
                id={`header-ts-desc-${log?.timesheetDetailId || index}`}
                text={log?.workDescription}
                className="header-timesheet-flyout-item-description"
              />
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="header-timesheet-panel">
      <button
        type="button"
        ref={toggleElementRef}
        id="header-timesheet-action"
        className="timesheet-action header-icon-part"
        onClick={toggleDropdown}
      >
        <Iconify icon="tdesign:time" />
      </button>
      <Tooltip
        label="Timesheet"
        targetId="header-timesheet-action"
        position="bottom"
        delayShow={200}
      />

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="header-timesheet-flyout"
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 1000,
            }}
          >
            <div className="header-timesheet-flyout-header">
              <div className="header-timesheet-flyout-header-row">
                <h6 className="header-timesheet-flyout-title">{todayTitle}</h6>
                <div className="header-timesheet-flyout-header-actions">
                  <Buttons
                    buttonTypeClassName="refresh-btn header-timesheet-refresh-btn"
                    onClick={fetchTodayLogs}
                    buttonText=""
                    textWithIcon={true}
                    fontIcon={true}
                    iconName="ic:sharp-refresh"
                    titleText="Refresh"
                    isLoading={isInitialLoading || isRefreshing}
                  />
                  <span className={`header-timesheet-hours-pill ${hoursStatus}`}>
                    {totalHours}
                  </span>
                </div>
              </div>
              <div className="header-timesheet-flyout-meta">
                {isInitialLoading ? "Loading..." : entryLabel}
              </div>
            </div>
            <div className="header-timesheet-flyout-body">{renderBody()}</div>
            <div className="header-timesheet-flyout-footer">
              <Buttons
                buttonText="View All"
                buttonTypeClassName="outlined-theme-btn"
                onClick={handleViewAll}
              />
              <Buttons
                buttonText="Add Log"
                textWithIcon={true}
                fontIcon={true}
                iconName="ri:add-fill"
                buttonTypeClassName="theme-btn"
                onClick={handleAddLog}
              />
            </div>
          </div>,
          document.body
        )}

      {showTimesheetModal && (
        <CenterModel
          showModal={showTimesheetModal}
          handleToggleModal={handleCloseModal}
          modalTitle="Timesheet details"
          className="timelog-sidebar"
          modelSizeClass="timesheet-model"
        >
          <Suspense fallback={null}>
            <AddUpdateTimeLog
              isOpen={showTimesheetModal}
              defaultDate={new Date()}
              defaultStartTime={lastEndTime}
              timesheetData={null}
              onSuccess={handleAddSuccess}
              onClose={handleCloseModal}
            />
          </Suspense>
        </CenterModel>
      )}
    </div>
  );
};

export default HeaderTimesheetPanel;
